// Who may email / blast / enroll a lead, and which leads an agent may export. CommonJS, no imports
// (shared with functions/mcp via `npm run sync:mcp-shared`).
// Send/enroll (non-admins): only the ASSIGNED agent (assignedToId), or the OWNER who entered the lead
// themselves (ownerUid === createdBy === uid). Shared-only, assigned to someone else, co-agent: blocked.
function canMessageLead(c, uid, isAdmin) {
  if (isAdmin) return !!c;
  if (!c || !uid) return false;
  if (c.assignedToId === uid) return true;
  if (c.ownerUid === uid && c.createdBy === uid) return true;
  // Agent owner ("Owned by" = the agent who entered it) keeps full access after transferring the assignment;
  // Transfer keeps them on the lead's sharedWith so the database lets them read it.
  return c.createdBy === uid && c.leadGeneratedBy !== 'team' && !!(c.sharedWith && c.sharedWith[uid] === true) && !isTeamGenerated(c);
}
// Team leads: "Lead generated by: Team" (leadGeneratedBy 'team'), team ownership, or anything imported
// (BoldTrail source or boldtrailId, or an import by Kelly/team). Never exportable by agents.
function isTeamGenerated(c) {
  return !!c && (c.leadGeneratedBy === 'team' || c.ownerType === 'team' || !!c.imported || !!c.importedBy || !!c.importId
    || !!c.boldtrailId || /boldtrail/i.test(String(c.source || '')));
}
// CSV export (non-admins): only leads the agent owns because they entered them, never team leads.
function canExportOwnLead(c, uid) {
  return !!c && !!uid && c.ownerUid === uid && c.createdBy === uid && !isTeamGenerated(c);
}
const CSV_HEAD = ['Name', 'Email', 'Phone', 'Type', 'Status', 'Source', 'Tags', 'Area', 'Price range', 'Created'];
const cell = (v) => { const s = String(v == null ? '' : v); return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${(/^[=+\-@]/.test(s) ? "'" : '') + s.replace(/"/g, '""')}"` : s; };
function leadRow(c) {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.name || '';
  const money = (n) => (n === '' || n == null ? '' : `$${Number(n).toLocaleString('en-US')}`);
  const price = c.priceMin || c.priceMax ? `${money(c.priceMin) || 'Any'} - ${money(c.priceMax) || 'Any'}` : '';
  const area = [c.interestedCity, c.interestedDevelopment].filter(Boolean).join(', ');
  const status = c.archived ? 'Archived' : c.status || c.leadStatus || 'Active';
  const created = c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : '';
  return [name, c.email || '', c.phone || '', (c.contactTypes || []).join('; '), status, c.source || '', (c.tags || []).join('; '), area, price, created];
}
function ownLeadsCsv(contacts, uid, isAdmin) {
  const rows = (contacts || []).filter((c) => (isAdmin ? !!c : canExportOwnLead(c, uid))).map(leadRow);
  return { count: rows.length, csv: [CSV_HEAD, ...rows].map((r) => r.map(cell).join(',')).join('\r\n') };
}
// Lead generated by for a spreadsheet import: 'agent' only when an agent imports leads for themselves
// (not from BoldTrail). Admin/team imports, imports for someone else, and BoldTrail files are 'team'.
function importedLeadGeneratedBy({ isAdmin, uid, ownerUid, source } = {}) {
  return !isAdmin && uid && ownerUid === uid && !/boldtrail/i.test(String(source || '')) ? 'agent' : 'team';
}
// Leads list visibility for agents (Agent / Limited Agent): leads they are assigned to, own because they
// entered them, or that are shared with them (shared = view only, no email/text from Resi). Leads assigned
// to another agent and not shared stay hidden. Admin, Marketing Manager and TC keep their existing view.
const AGENT_VIEW_ROLES = ['Agent', 'limited_agent', 'Limited Agent'];
function agentSeesLead(c, uid) { return canMessageLead(c, uid, false) || !!(c && uid && c.sharedWith && c.sharedWith[uid] === true); }
// Shared-only: visible but read-only for messaging.
function isSharedOnlyFor(c, uid) { return agentSeesLead(c, uid) && !canMessageLead(c, uid, false); }
// "Owned by": Team for team/BoldTrail/Kelly-import leads, else the agent who created it. Set at creation; only
// admins change it (through leadGeneratedBy, which the database rules lock to admins).
function ownedByOf(c) {
  if (!c) return '';
  const team = isTeamGenerated(c); // BoldTrail, team and Kelly imports always count as Team
  if (team) return 'team';
  return c.createdBy || c.ownerUid || ''; // the agent who created it stays the owner after a hand-off
}
function visibleLeadsFor(contacts, uid, role) {
  if (!AGENT_VIEW_ROLES.includes(role)) return contacts || [];
  return (contacts || []).filter((c) => agentSeesLead(c, uid));
}
// The ASSIGNED agent drives automation: auto tasks go to them and campaign/auto emails go out as them
// (merge fields, signature, reply-to). Read at task/send time, so a new assignment applies to everything after it.
function assignedAgentOf(c, fallback) {
  if (!c) return fallback || null;
  return c.assignedToId || (c.ownerType !== 'team' && c.ownerId) || c.ownerUid || fallback || null;
}
function campaignSenderFor(c, users) {
  const id = assignedAgentOf(c, null);
  const u = id && users ? users[id] : null;
  if (!id || !u) return null;
  const name = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '';
  return { uid: id, name, email: u.email || '', phone: u.phone || '', signature: u.emailSignature || u.signature || '', replyTo: u.email ? [u.email] : [] };
}
// Pending campaign tasks for one lead that should move to the new assigned agent (done tasks stay put).
function campaignTasksToMove(tasks, contactId) {
  return Object.entries(tasks || {}).filter(([, t]) => t && t.crmContactId === contactId && t.source === 'crm-campaign' && t.status !== 'completed' && t.status !== 'done' && !t.completedAt).map(([id]) => id);
}
// Agent Leads filters. owned: '' | 'team' | 'me'. assigned: '' | 'me' | 'other' | 'other:<uid>'
// ('other' = leads I own that are assigned to another agent).
function agentLeadFilter(c, uid, { owned = '', assigned = '' } = {}) {
  if (!c) return false;
  const own = ownedByOf(c);
  if (owned === 'team' && own !== 'team') return false;
  if (owned === 'me' && own !== uid) return false;
  const to = assignedAgentOf(c, null);
  if (assigned === 'me' && to !== uid) return false;
  if (assigned.startsWith('other')) {
    if (own !== uid || !to || to === uid) return false;
    const who = assigned.split(':')[1];
    if (who && to !== who) return false;
  }
  return true;
}
// Other agents holding leads I own (for the "Another agent: <name>" options).
function handedOffAgents(contacts, uid) {
  return [...new Set((contacts || []).filter((c) => agentLeadFilter(c, uid, { assigned: 'other' })).map((c) => assignedAgentOf(c, null)))];
}
module.exports = { agentLeadFilter, handedOffAgents, assignedAgentOf, campaignSenderFor, campaignTasksToMove, isSharedOnlyFor, ownedByOf, agentSeesLead, visibleLeadsFor, AGENT_VIEW_ROLES, importedLeadGeneratedBy, canMessageLead, isTeamGenerated, canExportOwnLead, ownLeadsCsv, CSV_HEAD };
